import React from "react";
import { CheckCircle, CalendarClock, BellRing, Receipt, Repeat } from "lucide-react";
import { companyInfo } from "../data/mock";

const plans = [
  {
    name: "Elke 4 weken",
    tag: "Intensief",
    for: "Bedrijfspanden, winkels en woningen langs een drukke weg.",
    perks: ["Hoogste frequentiekorting", "Altijd representatief", "Kozijnen standaard bijgehouden"],
  },
  {
    name: "Elke 6 – 8 weken",
    tag: "Meest gekozen",
    featured: true,
    for: "Ideaal voor de meeste woningen in Rosmalen en Den Bosch.",
    perks: ["Streeploos door pollen- én herfstseizoen", "Automatisch ingepland", "Voordeliger dan losse beurten"],
  },
  {
    name: "Elke 12 weken",
    tag: "Basis",
    for: "Vier keer per jaar schone ramen, zonder er aan te denken.",
    perks: ["Laagdrempelig starten", "Combineren met dakgoten of zonnepanelen", "Flexibel aan te passen"],
  },
];

const steps = [
  { icon: CalendarClock, title: "Kies uw frequentie", text: "Bereken uw prijs en kies hoe vaak wij komen." },
  { icon: BellRing, title: "Wij plannen automatisch", text: "U krijgt minimaal 24 uur vooraf bericht via e-mail of WhatsApp." },
  { icon: Receipt, title: "Betalen achteraf", text: "Na iedere beurt een duidelijke factuur. Aanpassen of pauzeren kan altijd in overleg." },
];

const Subscription = () => (
  <section id="abonnement" className="py-24 bg-gradient-to-br from-slate-900 via-sky-900 to-teal-900 relative overflow-hidden" aria-labelledby="abonnement-heading" data-testid="subscription-section">
    <div className="absolute -top-24 -right-24 w-96 h-96 bg-sky-500/10 rounded-full blur-3xl" />
    <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14">
        <span className="inline-flex items-center gap-2 px-4 py-1 bg-white/10 backdrop-blur-md text-white rounded-full text-sm font-semibold mb-4 border border-white/20">
          <Repeat className="w-4 h-4" /> Onderhoudsabonnement
        </span>
        <h2 id="abonnement-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
          Nooit meer aan uw ramen denken
        </h2>
        <p className="text-lg text-white/70 max-w-2xl mx-auto">
          Kies een vast schema. Wij plannen automatisch in, sturen vooraf bericht en u betaalt per beurt minder dan bij een losse afspraak.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
        {plans.map((plan) => (
          <div
            key={plan.name}
            data-testid={`plan-card-${plan.tag.toLowerCase().replace(/\s+/g, "-")}`}
            className={`relative rounded-3xl p-7 flex flex-col transition-transform hover:-translate-y-1 ${
              plan.featured
                ? "bg-white text-slate-800 shadow-2xl shadow-sky-500/20 md:-my-3"
                : "bg-white/10 backdrop-blur-md border border-white/15 text-white"
            }`}
          >
            <span className={`inline-block self-start text-xs font-bold uppercase tracking-wide rounded-full px-3 py-1 mb-4 ${plan.featured ? "bg-gradient-to-r from-sky-500 to-teal-500 text-white" : "bg-white/15 text-white/90"}`}>
              {plan.tag}
            </span>
            <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
            <p className={`mb-5 ${plan.featured ? "text-slate-600" : "text-white/70"}`}>{plan.for}</p>
            <ul className="space-y-2 mb-7 flex-1">
              {plan.perks.map((perk) => (
                <li key={perk} className="flex items-start gap-2 text-sm">
                  <CheckCircle className={`w-5 h-5 flex-shrink-0 ${plan.featured ? "text-teal-500" : "text-teal-300"}`} />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
            <a
              href="#calculator"
              className={`inline-flex items-center justify-center h-12 rounded-xl font-semibold transition-all ${
                plan.featured
                  ? "bg-gradient-to-r from-sky-500 to-teal-500 text-white hover:from-sky-600 hover:to-teal-600 shadow-lg shadow-sky-500/25"
                  : "bg-white/15 text-white hover:bg-white/25 border border-white/20"
              }`}
            >
              Bereken abonnementsprijs
            </a>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {steps.map(({ icon: Icon, title, text }, i) => (
          <div key={title} className="flex gap-4 items-start">
            <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0">
              <Icon className="w-6 h-6 text-teal-300" />
            </div>
            <div>
              <p className="text-white font-semibold mb-1">{i + 1}. {title}</p>
              <p className="text-white/65 text-sm leading-relaxed">{text}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="text-center text-white/60 text-sm mt-12">
        Liever persoonlijk advies over de juiste frequentie?{" "}
        <a href={`tel:${companyInfo.phoneClean}`} className="text-white font-semibold underline decoration-teal-400 underline-offset-4">
          Bel {companyInfo.phone}
        </a>
      </p>
    </div>
  </section>
);

export default Subscription;
